import { createContext, useContext, useMemo, useCallback, useRef, useEffect, type ReactNode } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import frTranslations from "./locales/fr.json";
import enTranslations from "./locales/en.json";

export type Lang = "fr" | "en";

interface LanguageContextValue {
  lang: Lang;
  t: (key: string, vars?: Record<string, string | number>) => string;
  switchLang: (newLang: Lang) => void;
  localePath: (path: string) => string;
  /**
   * Register the equivalent path in the other language for the current page.
   * Pages with localized slugs (e.g. blog posts, sector landings) should call
   * this so the language switcher navigates to the correct translated URL
   * instead of just toggling the /en prefix.
   *
   * Pass `null` to clear (e.g. when no translation exists → fallback applies).
   */
  registerAlternatePath: (otherLang: Lang, path: string | null) => void;
}

const translations: Record<Lang, Record<string, string>> = {
  fr: frTranslations,
  en: enTranslations,
};

const LanguageContext = createContext<LanguageContextValue | null>(null);

export function LanguageProvider({ children }: { children: ReactNode }) {
  const location = useLocation();
  const navigate = useNavigate();

  const lang: Lang = location.pathname.startsWith("/en/") || location.pathname === "/en" ? "en" : "fr";

  // Per-route map of alternate paths registered by the current page.
  // Reset whenever the URL changes so stale registrations from a previous
  // page never leak into the next navigation.
  const alternatesRef = useRef<{ key: string; fr: string | null; en: string | null }>({
    key: location.pathname,
    fr: null,
    en: null,
  });

  useEffect(() => {
    if (alternatesRef.current.key !== location.pathname) {
      alternatesRef.current = { key: location.pathname, fr: null, en: null };
    }
  }, [location.pathname]);

  const registerAlternatePath = useCallback(
    (otherLang: Lang, path: string | null) => {
      // Make sure registration is keyed to the current URL.
      if (alternatesRef.current.key !== location.pathname) {
        alternatesRef.current = { key: location.pathname, fr: null, en: null };
      }
      alternatesRef.current[otherLang] = path;
    },
    [location.pathname]
  );

  const t = useCallback(
    (key: string, vars?: Record<string, string | number>): string => {
      let value = translations[lang][key] ?? translations.fr[key] ?? key;
      if (vars) {
        Object.entries(vars).forEach(([k, v]) => {
          value = value.replace(new RegExp(`\\{\\{${k}\\}\\}`, "g"), String(v));
        });
      }
      return value;
    },
    [lang]
  );

  const localePath = useCallback(
    (path: string): string => {
      // Ensure path starts with /
      const cleanPath = path.startsWith("/") ? path : `/${path}`;
      return lang === "en" ? `/en${cleanPath}` : cleanPath;
    },
    [lang]
  );

  const switchLang = useCallback(
    (newLang: Lang) => {
      const currentPath = location.pathname;
      let targetPath: string;

      // 1) If the current page registered an explicit alternate URL for the
      //    target language, use it as-is. This is required for pages with
      //    localized slugs (blog posts, sector landings, …) where simply
      //    toggling the /en prefix produces a 404.
      const explicit =
        alternatesRef.current.key === currentPath
          ? alternatesRef.current[newLang]
          : null;

      if (explicit) {
        targetPath = explicit;
      } else if (newLang === "en") {
        // Add /en prefix
        targetPath = currentPath.startsWith("/en") ? currentPath : `/en${currentPath}`;
      } else {
        // Remove /en prefix
        targetPath = currentPath.startsWith("/en/")
          ? currentPath.slice(3)
          : currentPath === "/en"
          ? "/"
          : currentPath;
      }

      localStorage.setItem("kalyvox_lang", newLang);
      navigate(targetPath + location.search);
    },
    [location.pathname, location.search, navigate]
  );

  const value = useMemo(
    () => ({ lang, t, switchLang, localePath, registerAlternatePath }),
    [lang, t, switchLang, localePath, registerAlternatePath]
  );

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useTranslation() {
  const ctx = useContext(LanguageContext);
  if (!ctx) {
    throw new Error("useTranslation must be used within a LanguageProvider");
  }
  return ctx;
}

export function useLocalePath() {
  const { localePath } = useTranslation();
  return localePath;
}

/**
 * Hook for pages with localized slugs to declare the equivalent URL in the
 * other language. The language switcher will navigate there instead of just
 * toggling the /en prefix (which would produce broken URLs).
 *
 * Pass `null` to indicate no translation exists; the switcher will then
 * fall back to /blog (or /en/blog) as defined by the caller's choice.
 */
export function useRegisterAlternatePath(otherLang: Lang, path: string | null) {
  const { registerAlternatePath } = useTranslation();
  useEffect(() => {
    registerAlternatePath(otherLang, path);
    return () => registerAlternatePath(otherLang, null);
  }, [otherLang, path, registerAlternatePath]);
}
